import Booking from "../../../api/Booking";
import { useNavigate } from "react-router-dom";
import { TAG_COLORS } from "../../../utils/tagColors";
import Service from "../../../api/Service";
import User from "../../../api/User";
import { useCallback, useEffect, useState } from "react";
import { parseWithOffset } from "../../../utils/dayjsUtils";
import dayjs from "dayjs";
import {
    alpha,
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import FormattedTime from "../../FormattedTime";
import FormattedPrice from "../../FormattedPrice";
import { FONTS } from "../../../constants/styles";

const BookingItem = (props: {
    uuid: string;
    brand_id: string;
    booking: Booking;
    selected?: boolean;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const hex = theme.palette.primary.main;

    const { uuid, brand_id, booking } = props;

    const tagColors = TAG_COLORS;

    const [service, setService] = useState<Service | undefined>();
    const [user, setUser] = useState<User | undefined>();

    const requiresAttention =
        booking.miaCandidate &&
        !booking.confirmed &&
        parseWithOffset(booking.start).isAfter(dayjs());

    useEffect(() => {
        Service.info(uuid, brand_id, booking.service)
            .then(setService)
            .catch(console.error);
        User.info(uuid, brand_id, booking.user)
            .then(setUser)
            .catch(console.error);
    }, [uuid, brand_id, booking]);

    const onClick = useCallback(() => {
        navigate(`/calendar/appointment/${booking.key}`);
    }, [booking, navigate]);

    // Fall back to "New" when the user record has no status yet.
    const rawStatus = user ? user.status?.trim() || "New" : undefined;
    const tagColor =
        tagColors.find((tagObj) => tagObj.tag === rawStatus)?.color ??
        "#6D7475";

    // miaCandidate is the only reachable non-default state in this app's
    // current booking lifecycle: cancelled bookings are filtered out of the
    // calendar, and "done" is never set by any code path. So the tile renders
    // either as a standard active booking or as the warning-amber attention
    // variant when miaCandidate is true on a future, unconfirmed booking.
    const tileSx = requiresAttention
        ? {
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              borderLeft: `3px solid ${theme.palette.warning.main}`,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.32)}`,
              borderLeftWidth: 3,
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.04)",
          }
        : {
              bgcolor: alpha(hex, 0.1),
              borderLeft: `3px solid ${hex}`,
              border: `1px solid ${alpha(hex, 0.18)}`,
              borderLeftWidth: 3,
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.04)",
          };

    return (
        <Stack
            direction="row"
            width="100%"
            alignItems="stretch"
            spacing={1.5}
        >
            {/* Time column */}
            <Stack
                direction="column"
                justifyContent="space-between"
                alignItems="center"
                sx={{ flex: "0 0 auto", width: 44, pt: 1.5, pb: 1.5 }}
            >
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={600}
                    fontSize="13px"
                    color="text.secondary"
                >
                    {parseWithOffset(booking.start).format("HH:mm")}
                </Typography>
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={600}
                    fontSize="13px"
                    color="text.secondary"
                >
                    {parseWithOffset(booking.end).format("HH:mm")}
                </Typography>
            </Stack>

            {/* Tile */}
            <ButtonBase
                onClick={onClick}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "16px",
                    textAlign: "left",
                }}
            >
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    width="100%"
                    sx={{
                        borderRadius: "16px",
                        p: 1.5,
                        overflow: "hidden",
                        ...tileSx,
                    }}
                    spacing={1}
                >
                    {/* Service name + price */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        width="100%"
                        overflow="hidden"
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={600}
                            fontSize="15px"
                            lineHeight="20px"
                            color="text.primary"
                            noWrap
                            sx={{
                                mr: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {service?.name}
                        </Typography>
                        <Box sx={{ flex: "0 0 auto" }}>
                            <FormattedPrice
                                price={booking.price}
                                currency={service?.currency}
                            />
                        </Box>
                    </Stack>

                    {/* Avatar + client name + status pill */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        overflow="hidden"
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            overflow="hidden"
                            minWidth={0}
                            flex={1}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: hex,
                                    flex: "0 0 auto",
                                    backgroundImage: user?.image
                                        ? `url(${user.image})`
                                        : undefined,
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "cover",
                                    boxShadow: `0 0 0 2px ${alpha(
                                        "#ffffff",
                                        0.6
                                    )}`,
                                }}
                            />
                            <Typography
                                fontWeight={400}
                                fontSize="13px"
                                color="text.secondary"
                                noWrap
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    minWidth: 0,
                                }}
                            >
                                {user?.name}
                            </Typography>
                        </Stack>

                        {/* Status pill */}
                        {rawStatus && (
                            <Stack
                                sx={{
                                    bgcolor: alpha(tagColor, 0.18),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: "8px",
                                    flex: "0 0 auto",
                                    ml: 1,
                                }}
                            >
                                <Typography
                                    fontWeight={500}
                                    fontSize="11px"
                                    color={tagColor}
                                    noWrap
                                >
                                    {rawStatus}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {/* Duration */}
                    <Box sx={{ flex: "0 0 auto" }}>
                        <FormattedTime time={booking.duration} />
                    </Box>
                </Stack>
            </ButtonBase>
        </Stack>
    );
};

export default BookingItem;
